import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    Divider,
    Breadcrumbs,
    Link,
    Paper,
    Tooltip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Upload as UploadIcon,
    ContentCopy as ContentCopyIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import galleryService from '../../services/gallery.service';
import { AE_palette } from '../../style/theme';

const GalleryDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [gallery, setGallery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchGallery = async () => {
            try {
                const data = await galleryService.getGalleryById(id);
                setGallery(data);
            } catch (err) {
                console.error('Error fetching gallery details', err);
                setError('Failed to load gallery details');
            } finally {
                setLoading(false);
            }
        };
        fetchGallery();
    }, [id]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        // Could add snackbar here
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !gallery) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" color="error">{error || 'Gallery not found'}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/galleries')} sx={{ mt: 2 }}>
                    Back to Galleries
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, height: '100vh', overflow: 'auto' }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    onClick={() => navigate('/dashboard/galleries')}
                >
                    <ArrowBackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Galleries
                </Link>
                <Typography color="text.primary">{gallery.name}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Paper sx={{ p: 3, mb: 3, backgroundColor: '#00000060', borderRadius: 2 }}>
                <Grid container spacing={2} justifyContent="space-between" alignItems="flex-start">
                    <Grid size={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="h3" sx={{ color: AE_palette.seafoamGreen }}>
                                {gallery.name}
                            </Typography>
                            <Chip
                                label={gallery.status || 'Draft'}
                                color={gallery.status === 'published' ? 'success' : gallery.status === 'archived' ? 'default' : 'warning'}
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                            {gallery.description || 'No description provided.'}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid>
                                <Typography variant="caption" display="block" color="textSecondary">Target URL</Typography>
                                <Typography variant="body2">{gallery.targetUrl || 'None'}</Typography>
                            </Grid>
                            <Grid>
                                <Typography variant="caption" display="block" color="textSecondary">Short URL</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {gallery.shortCode ? `.../s/${gallery.shortCode}` : 'None'}
                                    </Typography>
                                    {gallery.shortCode && (
                                        <Tooltip title="Copy">
                                            <IconButton size="small" onClick={() => handleCopy(`http://localhost:3005/galleries/s/${gallery.shortCode}`)}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                            <Grid>
                                <Typography variant="caption" display="block" color="textSecondary">Created By</Typography>
                                <Typography variant="body2">{gallery.createdBy || 'System'}</Typography>
                            </Grid>
                            <Grid>
                                <Typography variant="caption" display="block" color="textSecondary">Last Updated</Typography>
                                <Typography variant="body2">{new Date(gallery.updatedAt).toLocaleDateString()}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" startIcon={<EditIcon />} size="small">
                                Edit
                            </Button>
                            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} size="small">
                                Delete
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Items Section */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">
                    Gallery Items ({gallery.items?.length || 0})
                </Typography>
                <Button variant="contained" startIcon={<UploadIcon />} color="primary">
                    Upload Item
                </Button>
            </Box>

            {!gallery.items || gallery.items.length === 0 ? (
                <Box
                    sx={{
                        p: 5,
                        border: '2px dashed rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        textAlign: 'center',
                        backgroundColor: '#00000020'
                    }}
                >
                    <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No items in this gallery yet
                    </Typography>
                    <Button variant="outlined" startIcon={<UploadIcon />}>
                        Upload your first item
                    </Button>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {/* Placeholder for items - since we only have strings/IDs primarily, we visualize them as basic cards */}
                    {gallery.items.map((item, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                            <Card sx={{ height: '100%', backgroundColor: '#1E1E1E' }}>
                                <Box
                                    sx={{
                                        height: 150,
                                        backgroundColor: '#333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="caption" color="textSecondary">Item Preview</Typography>
                                </Box>
                                <CardContent>
                                    <Typography variant="body2" noWrap title={item}>
                                        {item}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default GalleryDetails;
